import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Lucide from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import Seo from "../components/Seo";
import { Reveal, SectionHead } from "../components/Reveal";
import { SERVICES, TRUST_CARDS, SOLUTIONS, PROCESS_STEPS, WHY_CARDS, MARQUEE_ITEMS, ECOSYSTEM_FLOW } from "../data/content";
import { api } from "../api";

const Icon = ({ name, size = 22, className = "" }) => {
  const Cmp = Lucide[name] || Lucide.Circle;
  return <Cmp size={size} className={className} />;
};

const HERO_LINES = ["TURN", "ATTENTION", "INTO INFLUENCE."];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-grid noise-overlay" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 hero-aura" />
      <div className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-blue-600/10 blur-[120px]" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-cyan-500/8 blur-[110px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-32 pb-20 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center w-full">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="font-mono-accent text-xs uppercase tracking-[0.35em] text-blue-400 mb-7"
            data-testid="hero-eyebrow"
          >
            Where Media Meets Influence
          </motion.p>
          <h1 className="font-display font-black tracking-tight leading-[0.95] text-slate-50" data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className={`block text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem] ${i === 2 ? "text-gradient" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
            data-testid="hero-subheadline"
          >
            We build powerful media and social strategies that help brands get noticed, engage audiences and grow in the digital world.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/start"
              data-testid="hero-start-project-btn"
              className="group inline-flex items-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-all duration-300 active:scale-95 shadow-xl shadow-blue-600/30 pulse-glow"
            >
              Start a Project
              <Lucide.ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href="#services"
              data-testid="hero-explore-services-btn"
              className="inline-flex items-center gap-2.5 rounded-full border border-slate-700 hover:border-blue-500/60 text-slate-200 hover:text-blue-300 font-semibold px-8 py-4 transition-all duration-300 active:scale-95"
            >
              Explore Our Services
              <Lucide.ArrowDown size={16} />
            </a>
          </motion.div>
        </div>

        <motion.div style={{ y: visualY }} className="relative hidden lg:block" data-testid="hero-ecosystem-visual">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="glass-panel rounded-2xl p-7 relative"
          >
            <div className="flex items-center justify-between mb-6">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-slate-500">Media Ecosystem</p>
              <span className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500/70" />
                <span className="w-2 h-2 rounded-full bg-cyan-400/60" />
                <span className="w-2 h-2 rounded-full bg-slate-600" />
              </span>
            </div>
            <div className="relative">
              <svg className="absolute left-[17px] top-2 bottom-2 w-px h-[calc(100%-16px)]" preserveAspectRatio="none" viewBox="0 0 1 100">
                <line x1="0.5" y1="0" x2="0.5" y2="100" stroke="#3B82F6" strokeWidth="1" className="flow-line" opacity="0.5" />
              </svg>
              {ECOSYSTEM_FLOW.map((node, i) => (
                <motion.div
                  key={node}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + i * 0.16, duration: 0.5 }}
                  className="relative flex items-center gap-4 py-2.5"
                >
                  <span className={`relative z-10 w-9 h-9 rounded-xl flex items-center justify-center border ${i === ECOSYSTEM_FLOW.length - 1 ? "bg-blue-600 border-blue-400 text-white" : "bg-slate-900 border-blue-500/30 text-blue-400"}`}>
                    {i === ECOSYSTEM_FLOW.length - 1 ? <Lucide.TrendingUp size={16} /> : <span className="font-mono-accent text-[10px]">{String(i + 1).padStart(2, "0")}</span>}
                  </span>
                  <span className="text-sm font-semibold text-slate-200">{node}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {[
            { label: "Social Reach", value: "45M+", className: "-top-6 -left-10", delay: 0 },
            { label: "Engagement", value: "8.4x", className: "top-1/3 -right-8", delay: 1.2 },
            { label: "Campaigns", value: "120+", className: "bottom-16 -left-12", delay: 0.6 },
            { label: "Brand Growth", value: "+312%", className: "-bottom-6 right-6", delay: 1.8 },
          ].map((chip) => (
            <motion.div
              key={chip.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: [0, -10, 0] }}
              transition={{
                opacity: { delay: 1.2, duration: 0.5 },
                y: { delay: 1.2 + chip.delay, duration: 5, repeat: Infinity, ease: "easeInOut" },
              }}
              className={`absolute ${chip.className} glass-panel rounded-xl px-4 py-3`}
              data-testid={`hero-chip-${chip.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <p className="font-mono-accent text-[9px] uppercase tracking-[0.25em] text-slate-500">{chip.label}</p>
              <p className="font-display text-xl font-extrabold text-slate-50">{chip.value}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500"
      >
        <Lucide.ChevronsDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="relative border-y border-slate-800/60 bg-[#0A0F1C] py-6 overflow-hidden" data-testid="marquee-strip">
      <div className="animate-marquee flex whitespace-nowrap w-max">
        {items.map((item, i) => (
          <span key={i} className="font-display text-2xl sm:text-3xl font-bold text-slate-700/80 tracking-wide flex items-center">
            <span className="px-8">{item}</span>
            <Lucide.Asterisk size={22} className="text-blue-500/50" />
          </span>
        ))}
      </div>
    </div>
  );
}

function Trust() {
  return (
    <section className="py-20 lg:py-28" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          eyebrow="The Trendsight Standard"
          title={<>WE DON'T JUST CREATE CONTENT.<br /><span className="text-gradient">WE CREATE ATTENTION.</span></>}
          sub="Trendsight Media combines strategy, creativity, content, social media, influencer marketing, performance marketing and analytics into one growth engine for your brand."
          testid="trust-headline"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TRUST_CARDS.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.1}>
              <div
                className="group h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-7 hover:border-blue-500/50 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
                data-testid={`trust-card-${card.title.toLowerCase()}`}
              >
                <span className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                  <Icon name={card.icon} size={20} />
                </span>
                <h3 className="font-display text-lg font-bold text-slate-50 mt-5 tracking-wide">{card.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{card.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="py-20 lg:py-28 bg-[#0A0F1C] border-y border-slate-800/50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <SectionHead
            eyebrow="Services Marketplace"
            title={<>OUR DIGITAL <span className="text-gradient">GROWTH ENGINE</span></>}
            sub="Eight integrated capabilities, one accountable team. Mix and match, or run the full engine."
            testid="services-headline"
          />
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((service, i) => (
            <Reveal key={service.id} delay={(i % 4) * 0.08}>
              <div
                className="group h-full flex flex-col rounded-2xl border border-slate-800 bg-slate-900/60 p-6 hover:border-blue-500/50 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300"
                data-testid={`service-card-${service.id}`}
              >
                <span className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                  <Icon name={service.icon} size={20} />
                </span>
                <h3 className="font-display text-lg font-bold text-slate-50 mt-5 leading-snug">{service.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{service.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {service.items.map((item) => (
                    <span key={item} className="text-[10px] font-mono-accent uppercase tracking-wider text-slate-400 border border-slate-700/70 rounded-full px-2.5 py-1">
                      {item}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/start?service=${encodeURIComponent(service.title)}`}
                  data-testid={`service-explore-${service.id}`}
                  className="mt-auto pt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Explore Service
                  <Lucide.ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Solutions() {
  return (
    <section id="solutions" className="py-20 lg:py-28" data-testid="solutions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          eyebrow="Who We Build For"
          title={<>BUILT FOR EVERY <span className="text-gradient">STAGE OF GROWTH</span></>}
          sub="From first-time founders to national enterprises, our playbooks adapt to your stage, budget and ambition."
          testid="solutions-headline"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {SOLUTIONS.map((sol, i) => (
            <Reveal key={sol.title} delay={(i % 4) * 0.08} className={i === 6 ? "sm:col-span-2 lg:col-span-3 xl:col-span-1" : ""}>
              <div
                className="group h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-7 hover:border-blue-500/50 hover:-translate-y-1.5 transition-all duration-300"
                data-testid={`solution-card-${sol.title.toLowerCase().replace(/[\s&]+/g, "-")}`}
              >
                <Icon name={sol.icon} size={26} className="text-blue-400 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-display text-base font-bold text-slate-50 mt-4 tracking-wide">{sol.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{sol.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Process() {
  return (
    <section id="process" className="py-20 lg:py-28 bg-[#0A0F1C] border-y border-slate-800/50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          eyebrow="How We Work"
          title={<>FIVE STEPS. <span className="text-gradient">ONE GROWTH SYSTEM.</span></>}
          align="center"
          testid="process-headline"
        />
        <div className="mt-16 hidden lg:block">
          <div className="relative">
            <div className="absolute top-7 left-[10%] right-[10%] h-px bg-gradient-to-r from-blue-600/60 via-cyan-400/50 to-blue-600/60" />
            <div className="grid grid-cols-5 gap-6">
              {PROCESS_STEPS.map((step, i) => (
                <Reveal key={step.number} delay={i * 0.12}>
                  <div className="relative text-center group" data-testid={`process-step-${step.number}`}>
                    <span className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-900 border border-blue-500/30 flex items-center justify-center font-mono-accent text-blue-400 font-bold group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-400 transition-all duration-300">
                      {step.number}
                    </span>
                    <h3 className="font-display text-base font-bold text-slate-50 mt-5 tracking-widest">{step.title}</h3>
                    <p className="text-[13px] text-slate-400 mt-2 leading-relaxed max-w-[200px] mx-auto">{step.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-12 lg:hidden relative pl-8">
          <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-600/60 via-cyan-400/50 to-blue-600/60" />
          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={step.number} delay={i * 0.1}>
              <div className="relative pb-10 last:pb-0" data-testid={`process-step-mobile-${step.number}`}>
                <span className="absolute -left-8 top-0 w-8 h-8 rounded-lg bg-slate-900 border border-blue-500/30 flex items-center justify-center font-mono-accent text-[11px] text-blue-400 font-bold">
                  {step.number}
                </span>
                <h3 className="font-display text-base font-bold text-slate-50 tracking-widest">{step.title}</h3>
                <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{step.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const GROWTH_DATA = [
  { m: "Jan", reach: 42, engagement: 18 }, { m: "Feb", reach: 55, engagement: 24 }, { m: "Mar", reach: 61, engagement: 31 },
  { m: "Apr", reach: 84, engagement: 38 }, { m: "May", reach: 102, engagement: 47 }, { m: "Jun", reach: 128, engagement: 58 },
  { m: "Jul", reach: 156, engagement: 71 }, { m: "Aug", reach: 189, engagement: 86 },
];
const CHANNEL_DATA = [
  { ch: "Meta", roi: 4.2 }, { ch: "Google", roi: 3.6 }, { ch: "YouTube", roi: 3.1 }, { ch: "LinkedIn", roi: 2.8 }, { ch: "Creators", roi: 5.4 },
];
const DASH_STATS = [
  { label: "Reach", value: "12.4M", delta: "+38%" },
  { label: "Engagement", value: "6.9%", delta: "+1.8pt" },
  { label: "Leads", value: "4,812", delta: "+64%" },
  { label: "Conversions", value: "1,206", delta: "+52%" },
  { label: "Website Traffic", value: "318K", delta: "+91%" },
  { label: "Campaign ROI", value: "4.1x", delta: "+0.9x" },
];

function Dashboard() {
  return (
    <section id="analytics" className="py-20 lg:py-28 relative overflow-hidden" data-testid="dashboard-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[720px] h-[380px] bg-blue-600/8 blur-[130px] rounded-full" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative">
        <SectionHead
          eyebrow="Performance Dashboard"
          title={<>MARKETING THAT YOU CAN <span className="text-gradient">MEASURE.</span></>}
          sub="Every engagement ships with transparent dashboards covering reach, engagement, leads, conversions, traffic and ROI."
          align="center"
          testid="dashboard-headline"
        />
        <Reveal delay={0.15}>
          <div className="mt-14 glass-panel rounded-2xl p-5 sm:p-8" data-testid="dashboard-panel">
            <div className="flex items-center justify-between mb-6">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-slate-500">Growth Console</p>
              <p className="text-[11px] text-slate-500 border border-slate-700/70 rounded-full px-3 py-1">Illustrative sample data</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
              {DASH_STATS.map((s) => (
                <div key={s.label} className="rounded-xl border border-slate-800 bg-slate-900/70 p-4" data-testid={`dash-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="text-[10px] font-mono-accent uppercase tracking-widest text-slate-500">{s.label}</p>
                  <p className="font-display text-xl font-extrabold text-slate-50 mt-1.5">{s.value}</p>
                  <p className="text-[11px] text-emerald-400 mt-0.5">{s.delta}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5" data-testid="audience-growth-chart">
                <p className="text-sm font-semibold text-slate-300 mb-4">Audience Growth</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={GROWTH_DATA} margin={{ left: -18, right: 6, top: 4 }}>
                    <defs>
                      <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 10, fontSize: 12 }} labelStyle={{ color: "#94A3B8" }} />
                    <Area type="monotone" dataKey="reach" stroke="#3B82F6" strokeWidth={2.5} fill="url(#reachGrad)" name="Reach (K)" />
                    <Area type="monotone" dataKey="engagement" stroke="#06B6D4" strokeWidth={2} fill="none" name="Engagement (K)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5" data-testid="channel-roi-chart">
                <p className="text-sm font-semibold text-slate-300 mb-4">Channel ROI (illustrative)</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={CHANNEL_DATA} margin={{ left: -22, right: 6, top: 4 }}>
                    <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="ch" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 10, fontSize: 12 }} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                    <Bar dataKey="roi" fill="#2563EB" radius={[6, 6, 0, 0]} name="ROI (x)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WorkPreview() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/case-studies").then((r) => setItems(r.data.items.slice(0, 4))).catch(() => {});
  }, []);
  return (
    <section id="work" className="py-20 lg:py-28 bg-[#0A0F1C] border-y border-slate-800/50" data-testid="work-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <SectionHead
            eyebrow="Selected Work"
            title={<>IDEAS THAT MOVE <span className="text-gradient">BRANDS FORWARD.</span></>}
            sub="A look at how strategy, content and distribution come together. Demo case studies shown until client work is published."
            testid="work-headline"
          />
          <Link to="/work" data-testid="work-view-all-btn" className="group inline-flex items-center gap-2 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors shrink-0">
            View all case studies <Lucide.ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((cs, i) => (
            <Reveal key={cs.id} delay={i * 0.08}>
              <Link
                to={`/work/${cs.slug}`}
                data-testid={`work-card-${cs.slug}`}
                className="group block h-full rounded-2xl border border-slate-800 bg-slate-900/60 overflow-hidden hover:border-blue-500/50 hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="h-36 bg-gradient-to-br from-blue-600/25 via-slate-900 to-cyan-500/15 relative">
                  <span className="absolute bottom-3 left-4 font-mono-accent text-[10px] uppercase tracking-widest text-blue-300/80">{cs.industry}</span>
                  {cs.is_demo && <span className="absolute top-3 right-3 text-[9px] font-mono-accent uppercase tracking-widest text-slate-400 border border-slate-700 rounded-full px-2 py-0.5">Illustrative</span>}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-[15px] font-bold text-slate-100 leading-snug group-hover:text-blue-300 transition-colors">{cs.title}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(cs.results || []).slice(0, 2).map((r) => (
                      <span key={r.label} className="text-[10px] text-emerald-400/90 border border-emerald-500/25 rounded-full px-2 py-0.5">{r.value} {r.label}</span>
                    ))}
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const CREATOR_FLOW = ["Brand", "Campaign Brief", "Creator Discovery", "Content", "Distribution", "Analytics"];

function CreatorEcosystem() {
  return (
    <section className="py-20 lg:py-28 relative overflow-hidden" data-testid="creator-ecosystem-section">
      <div className="absolute -bottom-24 right-0 w-[480px] h-[380px] bg-cyan-500/8 blur-[120px] rounded-full" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHead
              eyebrow="Creator & Influencer Ecosystem"
              title={<>CONNECTING BRANDS <span className="text-gradient">WITH INFLUENCE.</span></>}
              sub="Trendsight Media matches your brand with the right creators, manages the entire campaign and reports performance down to the last conversion."
              testid="creator-headline"
            />
            <Reveal delay={0.2}>
              <Link
                to="/start?service=Influencer%20Marketing"
                data-testid="creator-campaign-btn"
                className="mt-9 group inline-flex items-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold px-8 py-4 transition-all duration-300 active:scale-95 shadow-xl shadow-blue-600/25"
              >
                Launch a Creator Campaign
                <Lucide.ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="glass-panel rounded-2xl p-7">
              {CREATOR_FLOW.map((node, i) => (
                <div key={node} className="relative">
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-4 py-3"
                  >
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center border ${i === 0 || i === CREATOR_FLOW.length - 1 ? "bg-blue-600/20 border-blue-400/40 text-blue-300" : "bg-slate-900 border-slate-700 text-slate-400"}`}>
                      <Icon name={["Building2", "FileText", "Search", "Clapperboard", "Radio", "BarChart3"][i]} size={17} />
                    </span>
                    <span className="text-sm font-semibold text-slate-200">{node}</span>
                  </motion.div>
                  {i < CREATOR_FLOW.length - 1 && (
                    <svg className="absolute left-5 top-full -mt-1 w-px h-4" viewBox="0 0 1 16">
                      <line x1="0.5" y1="0" x2="0.5" y2="16" stroke="#3B82F6" strokeWidth="1" className="flow-line" opacity="0.5" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Why() {
  return (
    <section className="py-20 lg:py-28 bg-[#0A0F1C] border-t border-slate-800/50" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          eyebrow="Why Trendsight"
          title={<>WHY BRANDS CHOOSE <span className="text-gradient">TRENDSIGHT</span></>}
          align="center"
          testid="why-headline"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHY_CARDS.map((card, i) => (
            <Reveal key={card.title} delay={(i % 3) * 0.1}>
              <div
                className="group h-full rounded-2xl border border-slate-800 bg-slate-900/60 p-7 hover:border-blue-500/50 hover:-translate-y-1.5 transition-all duration-300"
                data-testid={`why-card-${card.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <motion.span
                  whileHover={{ rotate: 8, scale: 1.1 }}
                  className="w-11 h-11 rounded-xl bg-blue-600/15 border border-blue-500/25 flex items-center justify-center text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300 inline-flex"
                >
                  <Icon name={card.icon} size={20} />
                </motion.span>
                <h3 className="font-display text-lg font-bold text-slate-50 mt-5">{card.title}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{card.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo />
      <Hero />
      <Marquee />
      <Trust />
      <Services />
      <Solutions />
      <Process />
      <Dashboard />
      <WorkPreview />
      <CreatorEcosystem />
      <Why />
    </main>
  );
}
